import { useState, useEffect } from "react"
import { Tag, Badge, Button } from "antd"
import { LeftOutlined } from "@ant-design/icons"
import ChatLayout from "@/Layout/ChatLayout"
import './index.scss'
import AIChat from "./components/AIChat"
import StockBaseInfo from "./stockBaseInfo"
import TimeLine from "./tinmeLine"
import GovernanceStructure from "./governanceStructure"
import FinancialData from "./financialData"
import IndustryAnalysis from "./industryAnalysis"
import ValuationAnchor from "./valuationAnchor"
import DynamicMonitoring from "./dynamicMonitoring"
import HistoryAnalysis from "./historyAnalysis"
import StockAI from "./components/StockAI"
import {
    getStockFinancedetail,
    getStockBaseMetrics,
    getStockKlines,
    getStockProfile,
    getStockGovernance,
    getStockQuote,
    getStockRevenueBreakdown,
    getStockReports,
} from '@/api'
import type {
    StockFinancialData,
    StockReport,
    StockBaseMetricsResponse,
    StockKlinePoint,
    StockProfileResponse,
    StockGovernanceResponse,
    StockQuoteResponse,
    RevenueBreakdownItem,
} from '@/types/stockPool'
import Reports from "./reports"

type KnowledgeBaseItem = {
    id: number
    title: string
    count?: number
}

const knowledgeBase: KnowledgeBaseItem[] = [{
    id: 1,
    title: '主体信息',
}, {
    id: 2,
    title: '时间线',
    count: 6,
}, {
    id: 3,
    title: '治理结构',
}, {
    id: 4,
    title: '财务数据',
}, {
    id: 6,
    title: '估值锚点',
}, {
    id: 7,
    title: '动态监控',
    count: 3,
}, {
    id: 8,
    title: '历史分析',
}, {
    id: 9,
    title: '研究文档',
}]

export default function StockDetailPage({ stockCode, onBack }: { stockCode: string, onBack: () => void }) {

    const [activeKey, setActiveKey] = useState(1)
    const [financialData, setFinancialData] = useState<StockFinancialData[]>([])
    const [stockMeta, setStockMeta] = useState<{ code: string; name: string | null }>({
        code: stockCode,
        name: null,
    })
    const [baseMetrics, setBaseMetrics] = useState<StockBaseMetricsResponse | null>(null)
    const [klines, setKlines] = useState<StockKlinePoint[]>([])
    const [profile, setProfile] = useState<StockProfileResponse | null>(null)
    const [governance, setGovernance] = useState<StockGovernanceResponse | null>(null)
    const [quote, setQuote] = useState<StockQuoteResponse | null>(null)
    const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueBreakdownItem | null>(null)
    const [report, setReport] = useState<StockReport | null>(null)
    const [historyCount, setHistoryCount] = useState(0)
    const [timelineCount, setTimelineCount] = useState(0)
    const [monitoringCount, setMonitoringCount] = useState(0)

    const handleClick = (key: number) => {
        setActiveKey(key)
    }

    const navItems = knowledgeBase.map((item) => {
        if (item.id === 2) return { ...item, count: timelineCount }
        if (item.id === 7) return { ...item, count: monitoringCount }
        if (item.id === 8) return { ...item, count: historyCount }
        return item
    })

    const quoteData = quote?.quote
    const lastKline = klines.length > 0 ? klines[klines.length - 1] : null
    const headerPrice = quoteData?.last_price ?? lastKline?.close ?? null
    const headerChangePct = (() => {
        if (quoteData?.prev_close_price) {
            return ((quoteData.last_price - quoteData.prev_close_price) / quoteData.prev_close_price) * 100
        }
        return lastKline?.change_pct ?? null
    })()
    const headerUp = (headerChangePct ?? 0) >= 0

    useEffect(() => {
        getStockFinancedetail(stockCode).then((data) => {
            setStockMeta({
                code: data.code || stockCode,
                name: data.name || null,
            })
            setFinancialData(data.items)
        })
        getStockBaseMetrics(stockCode).then((data) => { if (data) setBaseMetrics(data) }).catch((e) => console.error('[base-metrics]', e))
        getStockKlines(stockCode, 30).then(setKlines).catch((e) => console.error('[klines]', e))
        setRevenueBreakdown(null)
        getStockProfile(stockCode).then((data) => { if (data) setProfile(data) }).catch((e) => console.error('[profile]', e))
        getStockGovernance(stockCode).then((data) => { if (data) setGovernance(data) }).catch((e) => console.error('[governance]', e))
        getStockQuote(stockCode).then((data) => { if (data) setQuote(data) }).catch((e) => console.error('[quote]', e))
        getStockRevenueBreakdown(stockCode)
            .then((data) => setRevenueBreakdown(data?.item ?? null))
            .catch((e) => console.error('[revenue-breakdown]', e))
        setHistoryCount(0)
        setTimelineCount(0)
        setMonitoringCount(0)
        getStockReports({ code: stockCode, report_type: 'research', limit: 50 })
            .then((data) => setHistoryCount(data.total ?? data.items?.length ?? 0))
            .catch((e) => console.error('[history-reports]', e))
    }, [stockCode])

    const handleReportClick = (report: StockReport) => {
        setReport(report)
    }

    const back = () => {
        onBack()
    }

    return (
        <ChatLayout
            className="stock-detail-page page-container"
            headerClassName="stock-detail-page__header"
            header={
                <div className="stock-detail-page__header-content">
                    <div className="text-12 flex items-center gap-8 stock-detail-page__header-title">
                        <Button icon={<LeftOutlined />} style={{marginLeft: 0}} size="small" onClick={() => back()} />
                        <span style={{fontSize: 15, fontWeight: 700}}>{stockMeta.name || stockMeta.code}</span> 
                        <span className="color-3">{stockMeta.code}</span>
                        <span className="color-1 font-bold">
                            {headerPrice != null ? `￥${headerPrice.toFixed(2)}` : '--'}
                        </span>
                        {headerChangePct != null ? (
                            <Tag className={headerUp ? 'is-error' : 'is-success'}>
                                {headerUp ? '+' : ''}{headerChangePct.toFixed(2)}%
                            </Tag>
                        ) : (
                            <Tag className="is-default">--</Tag>
                        )}
                    </div>
                    <div className="stock-detail-page__header-knowledge-base flex items-center gap-8">
                        {
                            navItems.map((item) => (
                                <div
                                    className={`stock-detail-page__header-content-item flex items-center gap-4 flex-between ${item.id === activeKey ? 'is-active' : ''}`}
                                    key={item.id}
                                    onClick={() => handleClick(item.id)}
                                >
                                    <div className="stock-detail-page__aside-content-item-title">{item.title}</div>
                                    <Badge size="small" color='var(--bg-card-alt)' style={{ fontSize: 10, color: 'var(--text-3)' ,background: 'rgba(99,102,241,.15)'}} count={item.count ?? 0} />
                                </div>
                            ))
                        }
                    </div>
                </div>
            }
            contentClassName="stock-detail-page__content"
            content={
                <>
                    {activeKey === 1 ? (
                        <StockBaseInfo
                            metrics={baseMetrics?.metrics ?? null}
                            klines={klines}
                            profile={profile?.profile ?? null}
                            plates={profile?.plates ?? []}
                            quote={quote?.quote ?? null}
                            stockCode={stockCode}
                            revenueBreakdown={revenueBreakdown}
                        />
                    ) : activeKey === 2 ? (
                        <TimeLine stockCode={stockCode} onCountChange={setTimelineCount} />
                    ) : activeKey === 3 ? (
                        <GovernanceStructure
                            executives={governance?.executives ?? []}
                            holders={governance?.holders ?? []}
                        />
                    ) : activeKey === 4 ? (
                        <FinancialData stockCode={stockCode} data={financialData} />
                    ) : activeKey === 5 ? (
                        <IndustryAnalysis />
                    ) : activeKey === 6 ? (
                        <ValuationAnchor stockCode={stockCode} currentPrice={headerPrice} />
                    ) : activeKey === 7 ? (
                        <DynamicMonitoring stockCode={stockCode} onCountChange={setMonitoringCount} />
                    ) : activeKey === 8 ? (
                        <HistoryAnalysis stockCode={stockCode} onCountChange={setHistoryCount} />
                    ) : (
                        <Reports stockCode={stockCode} onReportClick={handleReportClick} />
                    )}
                </>
            }
            sider={
                activeKey === 9 ? (
                    <StockAI code={stockCode} report={report ?? null} />
                ) : <AIChat />
            }
            siderWidth={activeKey === 9 ? 560 : 400}
            resizableSider={activeKey === 9}
            minSiderWidth={380}
            maxSiderWidth={920}
        >
        </ChatLayout>
    )
}
